import {
  defaultDevice,
  init,
  numpy as np,
  type OwnedFunction,
} from "@jax-js/jax";
import { cachedFetch, opfs, safetensors } from "@jax-js/loaders";

import {
  createCompiledInference,
  type FlatParameters,
  type GraphOutput,
} from "./graph";
import type {
  Backend,
  InferenceInput,
  InferenceResult,
  KataGoChessModel,
  ModelIndex,
  ModelManifest,
  ModelProfile,
  ModelProgress,
  RawModelManifest,
  ModelSummary,
  ProgressCallback,
} from "./types";

function product(shape: number[]): number {
  return shape.reduce((result, dimension) => result * dimension, 1);
}

/**
 * Schema-v1 assets predate the game registry. Adapt them in memory so the
 * runtime can apply the same compatibility and head checks as schema v2.
 */
export function adaptModelManifest(raw: RawModelManifest): ModelManifest {
  if (raw.schemaVersion === 2) {
    if (
      !raw.compatibility?.length ||
      !raw.status ||
      !raw.architecture.policyHead
    ) {
      throw new Error("Malformed schema-v2 model manifest");
    }
    return raw;
  }
  return {
    ...raw,
    compatibility: [{ gameId: "chess", variantId: "standard" }],
    status: { trained: true, label: "Trained checkpoint" },
    architecture: {
      ...raw.architecture,
      policyHead: { kind: "generic-dense" },
    },
  };
}

function asUrl(path: string | URL, base?: string | URL): URL {
  const fallback =
    typeof document === "undefined" ? "http://localhost/" : document.baseURI;
  return new URL(path, base ?? fallback);
}

async function fetchJson<T>(url: URL): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  return (await response.json()) as T;
}

async function sha256(data: Uint8Array): Promise<string> {
  const owned = Uint8Array.from(data);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    owned.buffer,
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

async function cacheContains(url: string): Promise<boolean | null> {
  try {
    return (await opfs.info(url)) !== null;
  } catch {
    return null;
  }
}

async function fetchWithCache(
  url: URL,
  onProgress: (loaded: number) => void,
): Promise<Uint8Array<ArrayBuffer>> {
  try {
    return await cachedFetch(url, undefined, ({ loadedBytes }) =>
      onProgress(loadedBytes),
    );
  } catch (error) {
    // OPFS is unavailable in private browsing and non-browser test runtimes.
    console.warn("OPFS cache unavailable; using a direct fetch.", error);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
    const data = new Uint8Array(await response.arrayBuffer());
    onProgress(data.byteLength);
    return data;
  }
}

function softmax(logits: Float32Array): Float32Array {
  const maximum = Math.max(...logits);
  const probabilities = new Float32Array(logits.length);
  let denominator = 0;
  for (let index = 0; index < logits.length; index += 1) {
    const probability = Math.exp((logits[index] ?? 0) - maximum);
    probabilities[index] = probability;
    denominator += probability;
  }
  for (let index = 0; index < probabilities.length; index += 1) {
    probabilities[index] = (probabilities[index] ?? 0) / denominator;
  }
  return probabilities;
}

class LoadedModel implements KataGoChessModel {
  readonly id: string;
  readonly backend: Backend;
  readonly cacheStatus: ModelProgress["cache"];
  readonly #parameters: FlatParameters;
  readonly #inputShape: [number, number, number, number];
  readonly #legalMaskShape: [number, number];
  readonly #observationSize: number;
  readonly #actionCount: number;
  readonly #compiled: OwnedFunction<
    (observation: np.Array, legalActionMask: np.Array) => GraphOutput
  >;
  #compiledOnce = false;

  constructor(
    id: string,
    backend: Backend,
    cacheStatus: ModelProgress["cache"],
    parameters: FlatParameters,
    manifest: ModelManifest,
  ) {
    this.id = id;
    this.backend = backend;
    this.cacheStatus = cacheStatus;
    this.#parameters = parameters;
    this.#inputShape = manifest.architecture.inputShape;
    this.#legalMaskShape = manifest.architecture.legalMaskShape;
    this.#observationSize = product(this.#inputShape);
    this.#actionCount = manifest.architecture.actionSpace;
    this.#compiled = createCompiledInference(parameters, manifest.architecture);
  }

  async infer(input: InferenceInput): Promise<InferenceResult> {
    if (input.observation.length !== this.#observationSize) {
      throw new RangeError(
        `observation must contain ${this.#observationSize} float32 values`,
      );
    }
    if (input.legalActionMask.length !== this.#actionCount) {
      throw new RangeError(
        `legalActionMask must contain ${this.#actionCount} values`,
      );
    }

    const observation = np.array(input.observation, {
      dtype: np.float32,
      shape: this.#inputShape,
      device: this.backend,
    });
    const maskValues = new Int32Array(this.#actionCount);
    for (let index = 0; index < this.#actionCount; index += 1) {
      maskValues[index] = input.legalActionMask[index] ? 1 : 0;
    }
    const legalMask = np.array(maskValues, {
      dtype: np.bool,
      shape: this.#legalMaskShape,
      device: this.backend,
    });

    const started = performance.now();
    let stage = "graph execution";
    let rawData: ArrayBufferView;
    let wdlData: ArrayBufferView;
    try {
      const [raw, wdl] = this.#compiled(observation, legalMask);
      // Both outputs share one pending JIT execution. Await them sequentially
      // so two data() calls do not race the same traced program in the browser.
      stage = "policy readback";
      rawData = await raw.data();
      stage = "value readback";
      wdlData = await wdl.data();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`${this.backend} inference ${stage} failed: ${message}`, {
        cause: error,
      });
    }
    const elapsed = performance.now() - started;

    const rawPolicyLogits = new Float32Array(rawData as Float32Array);
    const maskedPolicyLogits = rawPolicyLogits.slice();
    for (let index = 0; index < this.#actionCount; index += 1) {
      if (!input.legalActionMask[index]) {
        maskedPolicyLogits[index] = -3.4028234663852886e38;
      }
    }
    const wdlLogits = new Float32Array(wdlData as Float32Array);
    const wdlProbabilities = softmax(wdlLogits);
    const value = (wdlProbabilities[0] ?? 0) - (wdlProbabilities[1] ?? 0);
    const compilationMs = this.#compiledOnce ? null : elapsed;
    this.#compiledOnce = true;

    return {
      rawPolicyLogits,
      maskedPolicyLogits,
      wdlLogits,
      wdlProbabilities,
      value,
      backend: this.backend,
      timing: {
        compilationMs,
        inferenceMs: compilationMs === null ? elapsed : 0,
        totalMs: elapsed,
      },
    };
  }

  dispose(): void {
    this.#compiled.dispose();
    for (const parameter of Object.values(this.#parameters)) parameter.dispose();
  }
}

export async function loadModel(
  baseUrl: string | URL,
  onProgress: ProgressCallback = () => undefined,
  modelId?: string,
  profile?: ModelProfile,
  options?: { forceBackend?: Backend },
): Promise<KataGoChessModel> {
  const normalizedBase = baseUrl.toString();
  const modelsUrl = asUrl(normalizedBase.endsWith("/") ? normalizedBase : `${normalizedBase}/`);
  onProgress({
    phase: "manifest",
    loadedBytes: 0,
    totalBytes: 0,
    cache: "checking",
  });
  const index = await fetchJson<ModelIndex>(asUrl("index.json", modelsUrl));
  const selectedId = modelId ?? index.defaultModel;
  const selected = index.models.find((model) => model.id === selectedId);
  if (!selected) throw new Error(`Model ${selectedId} is missing`);
  const manifestUrl = asUrl(selected.manifest, modelsUrl);
  const manifest = adaptModelManifest(
    await fetchJson<RawModelManifest>(manifestUrl),
  );
  if (
    ![1, 2].includes(manifest.schemaVersion) ||
    !/^b[1-9]\d*c[1-9]\d*(?:i[1-9]\d*)?nbt$/.test(
      manifest.architecture.preset,
    ) ||
    manifest.architecture.internalLength < 1 ||
    manifest.architecture.blocks < 1 ||
    !["generic-dense", "go-spatial-pass", "chess-spatial-v2"].includes(
      manifest.architecture.policyHead?.kind ?? "",
    )
  ) {
    throw new Error("Unsupported or malformed model manifest");
  }
  if (profile) {
    const compatible = manifest.compatibility?.some(
      ({ gameId, variantId }) =>
        gameId === profile.gameId && variantId === profile.variantId,
    );
    const [, height, width, channels] = manifest.architecture.inputShape;
    if (!compatible) {
      throw new Error(
        `Model ${manifest.id} is incompatible with ${profile.gameId}/${profile.variantId}`,
      );
    }
    if (
      height !== profile.observationShape[0] ||
      width !== profile.observationShape[1] ||
      channels !== profile.observationShape[2] ||
      manifest.architecture.actionSpace !== profile.actionCount
    ) {
      throw new Error(`Model ${manifest.id} shape does not match selected profile`);
    }
  }

  const shardUrls = manifest.shards.map((shard) =>
    asUrl(shard.file, manifestUrl),
  );
  const cacheChecks = await Promise.all(
    shardUrls.map((url) => cacheContains(url.toString())),
  );
  const knownChecks = cacheChecks.filter((value) => value !== null);
  let cacheStatus: ModelProgress["cache"];
  if (knownChecks.length === 0) cacheStatus = "unavailable";
  else if (knownChecks.every(Boolean)) cacheStatus = "hit";
  else if (knownChecks.every((value) => !value)) cacheStatus = "miss";
  else cacheStatus = "mixed";

  const totalBytes = manifest.shards.reduce((sum, shard) => sum + shard.bytes, 0);
  const loadedByShard = new Map<string, number>();
  const parsedShards = new Map<string, safetensors.File>();
  for (let index = 0; index < manifest.shards.length; index += 1) {
    const descriptor = manifest.shards[index];
    const url = shardUrls[index];
    if (!descriptor || !url) throw new Error("Manifest shard/url mismatch");
    const data = await fetchWithCache(url, (loaded) => {
      loadedByShard.set(descriptor.file, loaded);
      onProgress({
        phase: "download",
        loadedBytes: Array.from(loadedByShard.values()).reduce(
          (sum, value) => sum + value,
          0,
        ),
        totalBytes,
        shard: descriptor.file,
        cache: cacheStatus,
      });
    });
    onProgress({
      phase: "verify",
      loadedBytes: Array.from(loadedByShard.values()).reduce(
        (sum, value) => sum + value,
        0,
      ),
      totalBytes,
      shard: descriptor.file,
      cache: cacheStatus,
    });
    const actualHash = await sha256(data);
    if (actualHash !== descriptor.sha256) {
      throw new Error(
        `SHA-256 mismatch for ${descriptor.file}: ${actualHash} != ${descriptor.sha256}`,
      );
    }
    parsedShards.set(descriptor.file, safetensors.parse(data));
  }

  const available = await init("wasm", "webgpu");
  const backend: Backend = options?.forceBackend === "wasm"
    ? "wasm"
    : available.includes("webgpu") ? "webgpu" : "wasm";
  defaultDevice(backend);
  const parameters: FlatParameters = {};
  let hydrated = 0;
  for (const [name, descriptor] of Object.entries(manifest.tensors)) {
    if (descriptor.dtype !== "F32" || product(descriptor.shape) * 4 !== descriptor.nbytes) {
      throw new Error(`Invalid float32 tensor descriptor: ${name}`);
    }
    const reconstructed = new Float32Array(product(descriptor.shape));
    const rowElements = product(descriptor.shape.slice(1));
    for (const chunk of descriptor.chunks) {
      if (chunk.axis !== 0) throw new Error(`Unsupported chunk axis for ${name}`);
      const tensor = parsedShards.get(chunk.shard)?.tensors[chunk.key];
      if (!tensor || tensor.dtype !== "F32") {
        throw new Error(`Missing chunk ${chunk.shard}:${chunk.key}`);
      }
      const values = tensor.data as Float32Array;
      const expectedElements = (chunk.end - chunk.start) * rowElements;
      if (values.length !== expectedElements) {
        throw new Error(`Chunk length mismatch for ${name}:${chunk.key}`);
      }
      reconstructed.set(values, chunk.start * rowElements);
    }
    if ((await sha256(new Uint8Array(reconstructed.buffer))) !== descriptor.sha256) {
      throw new Error(`Byte reconstruction mismatch for ${name}`);
    }
    parameters[name] = np.array(reconstructed, {
      dtype: np.float32,
      shape: descriptor.shape,
      device: backend,
    });
    hydrated += descriptor.nbytes;
    onProgress({
      phase: "hydrate",
      loadedBytes: hydrated,
      totalBytes: manifest.parameterBytes,
      cache: cacheStatus,
    });
  }

  onProgress({
    phase: "ready",
    loadedBytes: manifest.parameterBytes,
    totalBytes: manifest.parameterBytes,
    cache: cacheStatus,
    backend,
  });
  return new LoadedModel(manifest.id, backend, cacheStatus, parameters, manifest);
}

export async function listModels(baseUrl: string | URL): Promise<{
  defaultModel: string;
  models: ModelSummary[];
}> {
  const normalizedBase = baseUrl.toString();
  const modelsUrl = asUrl(normalizedBase.endsWith("/") ? normalizedBase : `${normalizedBase}/`);
  const index = await fetchJson<ModelIndex>(asUrl("index.json", modelsUrl));
  return { defaultModel: index.defaultModel, models: index.models };
}
